import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from 'react';
import { loadWorkspace, saveWorkspace } from './workspace/workspacePersistence';
import {
  workspaceReducer,
  initialWorkspaceState,
  openPedigreeAction,
  setActiveAction,
  pinSessionAction,
  markEditedAction,
  setUnsavedAction,
  setLockAction,
  updateViewStateAction,
  renameSessionAction,
  setProbandNameAction,
  requestReleaseAction,
  markInaccessibleAction,
  closeSessionAction,
  closeOthersAction,
  closeAllAction,
  reorderTabsAction,
  getActiveSession
} from './workspace/workspaceReducer';

// App-level store for the multi-pedigree-tabs workspace: the set of open
// pedigrees, tab order, the active tab and the (single) preview tab. Mounted high
// in the tree (Dashboard, inside UserContext) so the open set survives navigating
// between the pedigree list, a report and back — the preview tab is only recycled
// when a DIFFERENT pedigree is opened, per the agreed UX. Modelled on
// AnnouncementsContext.js (context object + provider component + a hook).
//
// The reducer is pure; this provider is the impure edge that mints session ids and
// timestamps. useWorkspace() returns null when there is no provider (feature flag
// off), so callers that also work in single-pedigree mode must null-check.

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  // Restore the open tab SET synchronously (lazy init), so it is present before
  // the host mounts and re-opens the URL's family (which then just de-dupes).
  const [state, dispatch] = useReducer(
    workspaceReducer,
    initialWorkspaceState,
    (base) => {
      const restored = loadWorkspace();
      return restored
        ? {
            sessions: restored.sessions,
            activeSessionId: restored.activeSessionId,
            previewSessionId: restored.previewSessionId
          }
        : base;
    }
  );

  // Persist the open set on every change (best-effort; sessionStorage).
  useEffect(() => {
    saveWorkspace(state);
  }, [state]);

  // Monotonic, DOM-id-safe session id (also used as the pedigree instanceId that
  // namespaces SVG element ids). Alphanumeric + hyphen only.
  const counterRef = useRef(0);
  const mintId = useCallback(
    () => `ws${Date.now().toString(36)}-${counterRef.current++}`,
    []
  );

  const openPedigree = useCallback(
    (familyId, { title, activate = true, pinned = false } = {}) =>
      dispatch(
        openPedigreeAction({
          familyId,
          title,
          sessionId: mintId(),
          now: Date.now(),
          activate,
          pinned
        })
      ),
    [mintId]
  );

  const setActive = useCallback(
    (sessionId) => dispatch(setActiveAction(sessionId, Date.now())),
    []
  );
  const pinSession = useCallback(
    (sessionId) => dispatch(pinSessionAction(sessionId)),
    []
  );
  const markEdited = useCallback(
    (sessionId) => dispatch(markEditedAction(sessionId)),
    []
  );
  const setUnsaved = useCallback(
    (sessionId, hasUnsavedChanges) =>
      dispatch(setUnsavedAction(sessionId, hasUnsavedChanges)),
    []
  );
  const setLock = useCallback(
    (sessionId, lock) => dispatch(setLockAction(sessionId, lock)),
    []
  );
  const updateViewState = useCallback(
    (sessionId, patch) => dispatch(updateViewStateAction(sessionId, patch)),
    []
  );
  const renameSession = useCallback(
    (familyId, title) => dispatch(renameSessionAction(familyId, title)),
    []
  );
  const setProbandName = useCallback(
    (familyId, probandName) =>
      dispatch(setProbandNameAction(familyId, probandName)),
    []
  );
  const requestRelease = useCallback(
    (familyId) => dispatch(requestReleaseAction(familyId)),
    []
  );
  const markInaccessible = useCallback(
    (familyId) => dispatch(markInaccessibleAction(familyId)),
    []
  );
  const closeSession = useCallback(
    (sessionId) => dispatch(closeSessionAction(sessionId)),
    []
  );
  const closeOthers = useCallback(
    (sessionId) => dispatch(closeOthersAction(sessionId)),
    []
  );
  const closeAll = useCallback(() => dispatch(closeAllAction()), []);
  const reorderTabs = useCallback(
    (order) => dispatch(reorderTabsAction(order)),
    []
  );

  const value = useMemo(
    () => ({
      state,
      activeSession: getActiveSession(state),
      openPedigree,
      setActive,
      pinSession,
      markEdited,
      setUnsaved,
      setLock,
      updateViewState,
      renameSession,
      setProbandName,
      requestRelease,
      markInaccessible,
      closeSession,
      closeOthers,
      closeAll,
      reorderTabs
    }),
    [
      state,
      openPedigree,
      setActive,
      pinSession,
      markEdited,
      setUnsaved,
      setLock,
      updateViewState,
      renameSession,
      setProbandName,
      requestRelease,
      markInaccessible,
      closeSession,
      closeOthers,
      closeAll,
      reorderTabs
    ]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// Returns the workspace API, or null when there is no provider (feature flag off).
export const useWorkspace = () => useContext(WorkspaceContext);
